import HTTP from '../../services/HttpService'
import moment from 'moment/moment'
import { PLAN_TYPES } from '../../consts'
import { formatRawPhoneNum } from '../UtilService'

/*
  Map customer data from API to customer model.
  That will be used to pre-fill customer data in the onBoarding flow
 */
export function mapCustomerModelToPrefillData(
  { phoneCountryCode, customerData, availablePromoCodes },
  institutionData
) {
  let preFilledData = {
    email: customerData.email,
    firstName: customerData.firstName,
    lastName: customerData.lastName,
    phoneNumber: formatRawPhoneNum(customerData.phoneNumber),
    phoneCountryCode: phoneCountryCode || 'US',
    pro: customerData.pro,
    ssn: customerData.ssn,
    birthDate: customerData.birthDate,
    birthDateNotFormatted: customerData.birthDate
      ? moment.parseZone(customerData.birthDate).format('MM/DD/YYYY')
      : '',
    availablePromoCodes: availablePromoCodes,
    nationality: customerData.idCountry || '',
    passport: customerData.idNumber,
    expirationDate: customerData.idExpirationDate
      ? moment
          .parseZone(new Date(customerData.idExpirationDate))
          .format('MM/DD/YYYY')
      : '',
    nationalIdentifier: customerData.idGovNumber,
  }

  if (
    'customerAddressData' in customerData &&
    Object.keys(customerData.customerAddressData).length
  ) {
    const addressData = {
      city: customerData.customerAddressData.city,
      line1: customerData.customerAddressData.line1,
      line2: customerData.customerAddressData.line2,
      postalCode: customerData.customerAddressData.postalCode,
      state: customerData.customerAddressData.state || '',
      homeCountry: customerData.customerAddressData.country || '',
    }

    preFilledData = { ...preFilledData, ...addressData }
  }

  if (
    'customerBusinessData' in customerData &&
    Object.keys(customerData.customerBusinessData).length
  ) {
    const businessData = {
      // Business Data
      businessName: customerData.customerBusinessData.businessName,
      ein: customerData.customerBusinessData.ein,
      businessState: customerData.customerBusinessData.incorporationState, // businessState
      incomeType: customerData.customerBusinessData.mainIncomeSource, // incomeType
      segment: customerData.customerBusinessData.segment || '',
      sub_industry: customerData.customerBusinessData.naicsIndustry || '',
      businessOwners: customerData.customerBusinessData.businessUbos
        ? customerData.customerBusinessData.businessUbos.uboResponseList
        : [],
      businessId: customerData.customerBusinessData.externalId || null,
    }

    preFilledData = { ...preFilledData, ...businessData }
  }

  if (
    'businessAddressData' in customerData &&
    Object.keys(customerData.businessAddressData).length
  ) {
    const businessAddressData = {
      businessLine1: customerData.businessAddressData.line1,
      businessLine2: customerData.businessAddressData.line2,
      businessCity: customerData.businessAddressData.city,
      businessPostalCode: customerData.businessAddressData.postalCode,
      businessAddressState: customerData.businessAddressData.state || '',
      businessCountry: customerData.businessAddressData.country || '',
    }
    preFilledData = { ...preFilledData, ...businessAddressData }
  }
  if (
    'mailingAddressData' in customerData &&
    Object.keys(customerData.mailingAddressData).length
  ) {
    const mailingAddressData = {
      mailingLine1: customerData.mailingAddressData.line1,
      mailingLine2: customerData.mailingAddressData.line2,
      mailingCity: customerData.mailingAddressData.city,
      mailingPostalCode: customerData.mailingAddressData.postalCode,
      mailingAddressState: customerData.mailingAddressData.state || '',
    }
    preFilledData = { ...preFilledData, ...mailingAddressData }
  }
  if (institutionData) {
    preFilledData = { ...preFilledData, institutionData }
  }
  return preFilledData
}

export function getCustomerModel() {
  return {
    customerId: '',
    accessToken: '',
    stateToken: '',
    phoneNumber: '',
    phoneCountryCode: '',
    sourcePlatform: '',
    firstName: '',
    lastName: '',
    address: '',
    email: '',
    birthDateNotFormatted: '',
    adSource: '',
    startOnboarding: '',
    customerKey: '',
    leadExternalId: '',
    birthDate: null,
    line1: '',
    line2: '',
    city: '',
    password: '',
    state: '',
    postalCode: '',
    ...getEmptyBusinessAddress(),
    ...getEmptyMailingAddress(),
    ssn: '',
    ein: '',
    segment: '',
    sub_industry: '',
    sub_industry_title: '',
    businessName: '',
    businessRegisteredState: '',
    businessState: '',
    referralCode: '',
    sessionId: '',
    profile: 'SMART',
    isAccountTypeFlow: true,
    isPlanActivelySelected: false,
    incomeType: '',
    incomeSubType: '',
    incomeTypeFlow: true,
    sortedIncomeTypes: null,
    businessOwners: [],
    promoData: null,
    availablePromoCodes: {
      [PLAN_TYPES.PRO]: '',
      [PLAN_TYPES.PREMIUM]: '',
      [PLAN_TYPES.SMART]: '',
    },
    institutionData: {
      id: '',
      bankType: '',
    },
    ownershipPercent: 50,
    isNonUsSupported: false,
    nationality: '',
    nationalIdentifier: '',
    homeCountry: '',
    passport: '',
    nonUsQuestions: [
      {
        question:
          'Please describe the nature and purpose for your U.S. based business. More specifically, we will need to know:',
        answer: '',
        id: 'q1',
      },
      {
        question: 'Where your customers are primarily located?',
        answer: '',
        id: 'q2',
        radioGroupValue: '',
      },
      {
        question: 'Where your suppliers and vendors are primarily located?',
        answer: '',
        id: 'q3',
        radioGroupValue: '',
      },
      {
        question:
          'What is the expected <b> number of monthly transactions* </b> (both incoming <br> and outgoing) your business will process through its Lili account?',
        answer: '',
        id: 'q4',
      },
      {
        question:
          'What is the expected <b> transaction monthly volume* </b> (both incoming and <br> outgoing) your business will process through its Lili account?',
        answer: '',
        id: 'q5',
      },
    ],
    isPersonalAndBusinessAddressSame: false,
    isAnnual: false,
    isPreselectedAnnual: false,
  }
}

// Real mock data after manually onBoarding
export function getCustomerMockModel() {
  return {
    customerId: 'beec35ef-a7ab-4383-9287-9aa89e14ecf5',
    accessToken:
      'eyJraWQiOiIxbXNDVnRaTW85bExhYXpVbjBfYmxiUjFHck9hLXhUQmtCdWk2TGI4Z18wIiwiYWxnIjoiUlMyNTYifQ.eyJ2ZXIiOjEsImp0aSI6IkFULkk2SVhoTEhIaW1vQUVVRUVOal9Oa0VMQ2VkYm9waVUwUnRxX19oU2lqVUkiLCJpc3MiOiJodHRwczovL2Rldi0zNjE5OTAub2t0YXByZXZpZXcuY29tL29hdXRoMi9kZWZhdWx0IiwiYXVkIjoiYXBpOi8vZGVmYXVsdCIsImlhdCI6MTY2ODY3NTg3MiwiZXhwIjoxNjY4Njc2NzcyLCJjaWQiOiIwb2FoM2Y5Y2VyTk5ndTNodjBoNyIsInVpZCI6IjAwdTFmOG14NGQ1cjFJRXpjMGg4Iiwic2NwIjpbImVtYWlsIiwib3BlbmlkIl0sImF1dGhfdGltZSI6MTY2ODY3NTg3Miwic3ViIjoib3VvYWRAZ21haWwuY28iLCJFeHRlcm5hbElkIjoiYmVlYzM1ZWYtYTdhYi00MzgzLTkyODctOWFhODllMTRlY2Y1IiwiZ3JvdXBzIjpbIkV2ZXJ5b25lIiwiT25ib2FyZGluZyJdfQ.vU3wOZYmzjCW2GZkble0oEFSfV1wKHXSTFutv4Xh4DzSSU4u5IgO9oCREb6ytcZC29sTTzt-aPHTQ-26DuXZ--kV0br50bOcLqlByLAjSfLmSHAuG2CpGRgAopitgzw3N2wKYiv86VvcQj6N1mgeolcGH22h92eyG5V9pC_0NfMVZgazY9-IORQuNlhD2ygcZT4Ly4EZhIea_S6cP-uAWGvItEyXHueYdTZPROzMu5LoV_o20NWyIRc0LNTlch4BYSwY8dSK0UAXJWztN7WACh_Wl3LeEMVnZwCKobRqsQgySIS3-Z9IHKFbGKZ8EAB8zws_Hu5wzEKlUfohGIk4vQ',
    stateToken: '',
    phoneNumber: '2124232342',
    sourcePlatform: null,
    firstName: 'Awd',
    lastName: 'Awdaw',
    address: '',
    email: 'ouoad@gmail.co',
    birthDateNotFormatted: '08/08/1994',
    adSource:
      '{"CID":"GA1.1.777304951.1654782420","afUserId":"628f4901-33bb-4ba3-8260-974b8befd845-p"}',
    startOnboarding: 1668675854459,
    leadExternalId: '',
    birthDate: '1994-08-08T00:00:00.000Z',
    line1: 'Yona tzvi',
    line2: '',
    city: 'Rishon le zion',
    password: 'password',
    state: 'AZ',
    postalCode: '75548',
    businessAddressState: 'AZ',
    businessPostalCode: '75548',
    businessCity: 'Rishon le zion',
    businessLine2: '',
    businessLine1: 'Yona tzvi',
    ssn: '435345345',
    ein: '434534535',
    segment: 'Business Consultant',
    businessName: 'awdaw',
    businessRegisteredState: 'AR',
    sessionId: '',
    pro: true,
    isAccountTypeFlow: true,
    isPlanActivelySelected: false,
    incomeType: 'MMLLC',
    incomeSubType: '',
    incomeTypeFlow: true,
    sortedIncomeTypes: null,
    businessOwners: [
      {
        externalId: '2fb76f05-3da7-4fb9-a5b5-fac21dcdcc9c',
        email: 'jnaknawf@lili.com',
        phoneNumber: '+12125879345',
        firstName: 'Awda',
        lastName: 'Awdwa',
        ssn: '234242342',
        birthDate: '1994-08-07T21:00:00.000+00:00',
        customerAddressData: {
          city: 'Rishon Le Zion',
          state: 'AZ',
          line1: 'Yona tzvi',
          line2: '',
          postalCode: '75548',
        },
      },
      {
        externalId: '2fb76f05-3da7-4fb9-a5b5-fac21dcdcssc',
        email: 'jnaknawf@lili.com',
        phoneNumber: '+12125879345',
        firstName: 'ddd',
        lastName: 'aaa',
        ssn: '234242342',
        birthDate: '1994-08-07T21:00:00.000+00:00',
        customerAddressData: {
          city: 'Rishon Le Zion',
          state: 'AZ',
          line1: 'Yona tzvi',
          line2: '',
          postalCode: '75548',
        },
      },
    ],
    fromRoute: '/ob/account-type',
    isFeeABtestEnrolled: true,
    isABtestEnrolled: true,
    businessId: '8b5f0dc8-dc67-48c9-9e32-dff845126dbb',
    isPersonalAndBusinessAddressSame: true,
  }
}

export async function update({ method, endPoint, data = null, header }) {
  // This catch isn't really useless, but we don't have TS so eslint dosen't know that this returns a promise
  // eslint-disable-next-line no-useless-catch
  try {
    let res = await HTTP[method](endPoint, data, header)
    return res
  } catch (err) {
    throw err
  }
}

export function getEmptyBusinessAddress() {
  return {
    businessLine1: '',
    businessLine2: '',
    businessCity: '',
    businessAddressState: '',
    businessPostalCode: '',
    businessCountry: '',
  }
}

export function getEmptyMailingAddress() {
  return {
    mailingLine1: '',
    mailingLine2: '',
    mailingCity: '',
    mailingAddressState: '',
    mailingPostalCode: '',
  }
}



// WEBPACK FOOTER //
// ./src/services/entities/customerService.js